import type { IErrorToastContext } from "@/app/_utils/context/ErrorToastContext";
import type { IError } from "@/app/api/_utils/helpers/errorHandler.ts";

export interface IResponse<T> {
  "result": T;
}

export interface IHandleRequestConfig<T> {
  "url": string;
  "payload"?: FormData | object;
  "method"?: "DELETE" | "GET" | "PATCH" | "POST" | "PUT";
  "setToastError": IErrorToastContext["setToastError"];
  "callback"?: (arg: T) => void;
  "extra"?: () => void;
  "cleanup"?: () => void;
  "setCustomErr"?: (arg: string) => void;
  "abortController"?: AbortController;
  "debounceTime"?: number;
  "requestSlug"?: string;
}

const debounceTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const pendingRequests = new Map<string, AbortController>();

/**
 * Sends remote requests.
 *
 * @param config - Configuration object containing all request parameters.
 */
export default function handleRequest<T>(
  config: IHandleRequestConfig<T>
): void {
  const {
    url,
    payload,
    callback,
    setToastError,
    extra,
    cleanup,
    setCustomErr,
    abortController,
    debounceTime = 300,
    requestSlug,
  } = config;

  const key = requestSlug ?? url;

  const callApi = async (): Promise<T> => {
    // Create a new AbortController if one wasn't provided:
    const controller = abortController ?? new AbortController();

    try {
      // If there's already a pending request for this key, abort it:
      if (pendingRequests.has(key)) {
        const existingController = pendingRequests.get(key);

        if (existingController) {
          existingController.abort();
        }
      }

      // Store the current controller:
      pendingRequests.set(key, controller);

      const fetchConfig: RequestInit = {
        method: config.method ?? "POST",
        signal: controller.signal,
      }

      // Only add body for non-GET requests
      if (config.method !== "GET") {
        fetchConfig.body = payload instanceof FormData ? payload : JSON.stringify(payload)
        fetchConfig.headers = payload instanceof FormData
          ? undefined
          : { "Content-Type": "application/json" }
      }

      const response = await fetch(url, fetchConfig)

      // Clear the pending request reference once complete:
      if (pendingRequests.get(key) === controller) {
        pendingRequests.delete(key);
      }

      const data = (await response.json()) as IError | IResponse<T>;

      if ("error" in data) {
        throw new Error(data.error);
      }

      if (!("result" in data)) {
        throw new Error("Unexpected response format.");
      }

      return data.result;
    } catch (error) {
      // If this controller was replaced by a newer one, don't propagate
      // the error:
      if (
        pendingRequests.get(key) !== controller &&
        error instanceof DOMException &&
        error.name === "AbortError"
      ) {
        throw new Error("request-superseded");
      }

      // Special handling for aborted requests:
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error("request-aborted");
      }

      throw error instanceof Error
        ? error
        : new Error("Unknown error occurred.");
    }
  };

  const debouncedCallApi = (): void => {
    if (debounceTimeouts.has(key)) {
      const timeout = debounceTimeouts.get(key);

      if (timeout) {
        clearTimeout(timeout);
      }

      // If we're debouncing, also abort any pending request:
      if (pendingRequests.has(key)) {
        const existingController = pendingRequests.get(key);

        if (existingController) {
          existingController.abort();
          pendingRequests.delete(key);
        }
      }
    }

    const timeoutId = setTimeout(() => {
      callApi()
        .then((result) => {
          setToastError(null);

          if (callback) {
            callback(result);
          }

          if (extra) {
            extra();
          }

          if (setCustomErr) {
            setCustomErr("");
          }
        })
        .catch((error: unknown) => {
          // Don't show errors for aborted or superseded requests:
          if (
            error instanceof Error &&
            (error.message === "request-superseded" ||
              error.message === "request-aborted")
          ) {
            return;
          }

          const errorMessage =
            error instanceof Error ? error.message : "An error occurred.";

          if (setCustomErr) {
            setCustomErr(errorMessage);
          } else {
            setToastError(errorMessage);
          }
        })
        .finally(() => {
          if (cleanup) {
            cleanup();
          }
        });
    }, debounceTime);

    debounceTimeouts.set(key, timeoutId);
  };

  debouncedCallApi();
}
